import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Alert, Spinner, Toast } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import FooterTwo from '../../components/FooterTwo';
import { Styles } from './styles/profile.js';
import { AUTH_PROFILE_ENDPOINT } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const formSections = [
    {
        id: 'personal',
        title: 'Personal Information',
        fields: [
            { name: 'firstName', label: 'First Name', type: 'text' },
            { name: 'lastName', label: 'Last Name', type: 'text' },
            { name: 'email', label: 'Email', type: 'email' },
            { name: 'phone', label: 'Phone', type: 'text' },
            { name: 'alternateEmail', label: 'Alternate Email', type: 'email' },
            { name: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
            { name: 'addressLine1', label: 'Address Line 1', type: 'text' },
            { name: 'addressLine2', label: 'Address Line 2', type: 'text' },
            { name: 'city', label: 'City', type: 'text' },
            { name: 'state', label: 'State / Province', type: 'text' },
            { name: 'postalCode', label: 'Postal Code', type: 'text' },
            { name: 'country', label: 'Country', type: 'text' },
            { name: 'nationality', label: 'Nationality', type: 'text' }
        ]
    },
    {
        id: 'academic',
        title: 'Academic & Preferences',
        fields: [
            { name: 'highestEducation', label: 'Highest Education', type: 'text' },
            { name: 'graduationYear', label: 'Graduation Year', type: 'number' },
            { name: 'gpaScore', label: 'GPA Score', type: 'number', step: 'any' },
            { name: 'ieltsScore', label: 'IELTS Score', type: 'number', step: 'any' },
            { name: 'toeflScore', label: 'TOEFL Score', type: 'number', step: 'any' },
            { name: 'pteScore', label: 'PTE Score', type: 'number', step: 'any' },
            { name: 'duolingoScore', label: 'Duolingo Score', type: 'number', step: 'any' },
            { name: 'greScore', label: 'GRE Score', type: 'number', step: 'any' },
            { name: 'gmatScore', label: 'GMAT Score', type: 'number', step: 'any' },
            { name: 'satScore', label: 'SAT Score', type: 'number', step: 'any' },
            { name: 'actScore', label: 'ACT Score', type: 'number', step: 'any' },
            { name: 'workExperienceYears', label: 'Years of Work Experience', type: 'number', step: 'any' },
            { name: 'currentJobTitle', label: 'Current Job Title', type: 'text' },
            { name: 'currentEmployer', label: 'Current Employer', type: 'text' },
            { name: 'preferredStudyLevel', label: 'Preferred Study Level', type: 'text' },
            { name: 'preferredCountry', label: 'Preferred Country', type: 'text' },
            { name: 'preferredIntake', label: 'Preferred Intake', type: 'text' },
            { name: 'budgetRange', label: 'Budget Range', type: 'text' },
            { name: 'additionalNotes', label: 'Additional Notes', type: 'textarea' }
        ]
    }
];

const editableFields = formSections.flatMap((section) => section.fields);

const initialState = editableFields.reduce((acc, field) => {
    acc[field.name] = '';
    return acc;
}, {});

const groupFields = (fields) => {
    const groups = [];
    let chunk = [];
    fields.forEach((field, index) => {
        chunk.push(field);
        if (chunk.length === 2 || index === fields.length - 1) {
            groups.push(chunk);
            chunk = [];
        }
    });
    return groups;
};

const mapUserToFormValues = (userData) => {
    const mappedValues = { ...initialState };
    if (!userData) {
        return mappedValues;
    }

    editableFields.forEach(({ name, type }) => {
        const rawValue = userData[name];
        if (rawValue === undefined || rawValue === null) {
            return;
        }

        if (name === 'dateOfBirth' && rawValue) {
            const parsed = new Date(rawValue);
            if (!Number.isNaN(parsed.getTime())) {
                mappedValues[name] = parsed.toISOString().split('T')[0];
            }
            return;
        }

        if (type === 'number' && rawValue !== '') {
            mappedValues[name] = rawValue.toString();
            return;
        }

        mappedValues[name] = rawValue;
    });

    return mappedValues;
};

const Profile = () => {
    const history = useHistory();
    const { user, loading: authLoading, refreshUser } = useAuth();
    const [formValues, setFormValues] = useState(initialState);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [toastState, setToastState] = useState({ show: false, message: '' });

    useEffect(() => {
        if (!authLoading && !user) {
            history.push(`${process.env.PUBLIC_URL || ''}/login`);
        }
    }, [authLoading, user, history]);

    useEffect(() => {
        if (user) {
            setFormValues(mapUserToFormValues(user));
        }
    }, [user]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormValues((previous) => ({
            ...previous,
            [name]: value
        }));
        setError('');
        setToastState((prev) => ({ ...prev, show: false }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting) {
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('You must be logged in to update your profile.');
            }

            const body = {};
            editableFields.forEach(({ name, type }) => {
                const rawValue = formValues[name];
                if (rawValue === undefined || rawValue === null) {
                    body[name] = '';
                    return;
                }

                if (type === 'number') {
                    if (rawValue === '') {
                        body[name] = '';
                    } else {
                        const parsed = Number(rawValue);
                        body[name] = Number.isNaN(parsed) ? '' : parsed;
                    }
                    return;
                }

                if (type !== 'textarea' && type !== 'date') {
                    body[name] = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
                    return;
                }

                body[name] = rawValue;
            });

            const response = await fetch(AUTH_PROFILE_ENDPOINT, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                const message = await response.json().catch(() => ({}));
                throw new Error(message.message || 'Unable to update profile.');
            }

            const result = await response.json();
            if (result?.user) {
                localStorage.setItem('authUser', JSON.stringify(result.user));
            }
            setToastState({
                show: true,
                message: result?.message || 'Profile updated successfully.'
            });
            if (typeof refreshUser === 'function') {
                await refreshUser();
            }
        } catch (err) {
            setError(err.message || 'Unable to update profile.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleNext = () => {
        setError('');
        if (currentStep < formSections.length - 1) {
            setCurrentStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        setError('');
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const sectionFieldGroups = useMemo(
        () => groupFields(formSections[currentStep].fields),
        [currentStep]
    );

    return (
        <Styles>
            <div className="main-wrapper profile-page">
                <HeaderTwo />
                <BreadcrumbBox title="My Profile" />

                <section className="profile-area">
                    <Container>
                        <Row>
                            <Col md="12">
                                <div className="profile-card">
                                    <h4 className="section-title">{formSections[currentStep].title}</h4>
                                    {authLoading ? (
                                        <div className="text-center my-4">
                                            <Spinner animation="border" role="status" />
                                        </div>
                                    ) : (
                                        <>
                                            {error && <Alert variant="danger">{error}</Alert>}
                                            <form onSubmit={handleSubmit}>
                                                {sectionFieldGroups.map((group) => (
                                                    <div className="form-row" key={group.map((field) => field.name).join('-')}>
                                                        {group.map((field) => (
                                                            <div className="form-group" key={field.name}>
                                                                <label htmlFor={field.name}>{field.label}</label>
                                                                {field.type === 'textarea' ? (
                                                                    <textarea
                                                                        id={field.name}
                                                                        name={field.name}
                                                                        value={formValues[field.name] || ''}
                                                                        onChange={handleChange}
                                                                        disabled={submitting}
                                                                    />
                                                                ) : (
                                                                    <input
                                                                        id={field.name}
                                                                        name={field.name}
                                                                        type={field.type}
                                                                        value={formValues[field.name] || ''}
                                                                        onChange={handleChange}
                                                                        disabled={submitting}
                                                                        step={field.step}
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                        {group.length === 1 && (
                                                            <div className="form-group" key={`${group[0].name}-placeholder`} />
                                                        )}
                                                    </div>
                                                ))}

                                                <div className="form-actions">
                                                    <button
                                                        type="button"
                                                        className="back-btn"
                                                        onClick={handleBack}
                                                        disabled={submitting || authLoading || currentStep === 0}
                                                    >
                                                        Back
                                                    </button>
                                                    {currentStep < formSections.length - 1 ? (
                                                        <button
                                                            type="button"
                                                            onClick={handleNext}
                                                            disabled={submitting || authLoading}
                                                        >
                                                            Next
                                                        </button>
                                                    ) : (
                                                        <button type="submit" disabled={submitting}>
                                                            {submitting ? <Spinner animation="border" size="sm" /> : 'Save Changes'}
                                                        </button>
                                                    )}
                                                </div>
                                            </form>
                                        </>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>

                <FooterTwo />
            </div>
            <div className="toast-wrapper">
                <Toast
                    onClose={() => setToastState({ show: false, message: '' })}
                    show={toastState.show}
                    delay={4000}
                    autohide
                    className="bg-success text-white"
                >
                    <Toast.Body>{toastState.message}</Toast.Body>
                </Toast>
            </div>
        </Styles>
    );
};

export default Profile;
